import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel';
import { TOKEN_REFRESH_TIMEOUT_MS } from '../authCoordination';
import type { TokenRecoveryTrigger } from '../tokenRecoveryDiagnostics';
import {
  TokenRefreshCoordinator,
  type TokenRefreshCallback,
  type TokenUpdateCallback,
  type TokenRefreshResultCallback
} from './TokenRefreshCoordinator';
import type {
  TokenRefreshSuccess,
  TokenRefreshAttemptResult,
  PendingLeadershipWait,
  TabInfo,
  BroadcastMessage,
  BroadcastMessageHandler,
  LeadershipChangeCallback
} from './types';

export class TabCoordination {
  private readonly tokenRefresh: TokenRefreshCoordinator;

  private readonly channel: BroadcastChannel;

  private elector: LeaderElector;

  private onLeadershipChangeCallback: LeadershipChangeCallback | null = null;

  private readonly leadershipChangeCallbacks: Set<LeadershipChangeCallback> = new Set();

  private readonly messageHandlers: Map<string, Set<BroadcastMessageHandler>> = new Map();

  private isLeadershipActive: boolean = false;

  private pendingLeadershipWait: PendingLeadershipWait | null = null;

  private tabId: string;

  private activeTabs: Map<string, number> = new Map();

  private currentLeaderId: string | null = null;

  private heartbeatInterval: number | null = null;

  private visibilityTimeout: number | null = null;

  private isDestroyed: boolean = false;

  private readonly TAB_TIMEOUT_MS = 5000;

  private readonly HEARTBEAT_INTERVAL_MS = 2000;

  private readonly handleBeforeUnload = () => {
    if (this.isLeadershipActive) {
      this.elector.die();
    }
  };

  private readonly handleVisibilityChange = () => {
    if (this.isDestroyed || document.hidden) {
      return;
    }

    this.activeTabs.set(this.tabId, Date.now());
    this.broadcastHeartbeat();

    if (!this.isLeadershipActive) {
      if (this.visibilityTimeout !== null) {
        clearTimeout(this.visibilityTimeout);
      }
      this.visibilityTimeout = window.setTimeout(() => {
        this.requestLeaderInfo();
        this.ensureLeadership();
        this.visibilityTimeout = null;
      }, 500);
    }
  };

  private readonly handleChannelMessage = (messageEvent: BroadcastMessage) => {
    if (this.isDestroyed) {
      return;
    }

    const { type, data } = messageEvent;

    switch (type) {
      case 'TAB_HEARTBEAT':
        this.handleTabHeartbeat(data as { tabId: string; isLeader?: boolean });
        break;
      case 'LEADER_ANNOUNCEMENT':
        this.handleLeaderAnnouncement(data as { leaderId: string });
        break;
      case 'LEADER_REQUEST':
        this.handleLeaderRequest();
        break;
      default:
        if (!this.tokenRefresh.handleMessage(messageEvent)) {
          this.handleCustomMessage(type, data);
        }
        break;
    }
  };

  constructor() {
    this.channel = new BroadcastChannel('chat-token-coordination');
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.elector = createLeaderElection(this.channel, {
      fallbackInterval: 2000,
      responseTime: 1000
    });

    this.tokenRefresh = new TokenRefreshCoordinator({
      tabId: this.tabId,
      isLeader: () => this.isLeadershipActive,
      getLeaderId: () => this.currentLeaderId,
      ensureLeadership: () => this.ensureLeadership(),
      postMessage: message => {
        this.channel.postMessage(message);
      }
    });

    this.activeTabs.set(this.tabId, Date.now());
    this.setupEventListeners();
    this.initializeLeadership();
    this.startHeartbeat();

    this.requestLeaderInfo();
  }

  private setupEventListeners() {
    this.channel.addEventListener('message', this.handleChannelMessage);

    this.elector.onduplicate = () => {
      console.warn('Duplicate leader detected! Re-electing...');
    };

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private async initializeLeadership() {
    try {
      await this.elector.awaitLeadership();

      if (this.isDestroyed) {
        return;
      }
      this.isLeadershipActive = true;
      this.currentLeaderId = this.tabId;
      this.broadcastLeaderAnnouncement();
      this.notifyLeadershipChange(true);
    } catch (_error) {
      console.error('Tab leadership election failed');
    }
  }

  private notifyLeadershipChange(isLeader: boolean) {
    if (isLeader) {
      this.finishLeadershipWait(true);
    }

    if (this.onLeadershipChangeCallback) {
      this.onLeadershipChangeCallback(isLeader);
    }

    this.leadershipChangeCallbacks.forEach(callback => callback(isLeader));
  }

  private handleCustomMessage(type: string, data: unknown) {
    const handlers = this.messageHandlers.get(type);

    if (!handlers?.size) {
      console.warn('Unknown message type received:', type);
      return;
    }

    handlers.forEach(handler => handler(data));
  }

  private handleLeaderAnnouncement(data: { leaderId: string }) {
    if (data.leaderId) {
      this.currentLeaderId = data.leaderId;
      this.finishLeadershipWait(true);
    }
  }

  private handleLeaderRequest() {
    if (this.isLeadershipActive) {
      this.broadcastLeaderAnnouncement();
    }
  }

  private broadcastLeaderAnnouncement(): void {
    this.channel.postMessage({
      type: 'LEADER_ANNOUNCEMENT',
      data: { leaderId: this.tabId }
    });
  }

  private requestLeaderInfo() {
    this.channel.postMessage({
      type: 'LEADER_REQUEST',
      data: {}
    });
  }

  private handleTabHeartbeat(data: { tabId: string; isLeader?: boolean }) {
    if (data.tabId && data.tabId !== this.tabId) {
      this.activeTabs.set(data.tabId, Date.now());
      if (data.isLeader) {
        this.currentLeaderId = data.tabId;
        this.finishLeadershipWait(true);
      }
    }
  }

  private startHeartbeat() {
    this.heartbeatInterval = window.setInterval(() => {
      this.broadcastHeartbeat();
      this.cleanupInactiveTabs();
    }, this.HEARTBEAT_INTERVAL_MS);
  }

  private broadcastHeartbeat(): void {
    this.channel.postMessage({
      type: 'TAB_HEARTBEAT',
      data: {
        tabId: this.tabId,
        isLeader: this.isLeadershipActive
      }
    });
  }

  private cleanupInactiveTabs() {
    const now = Date.now();
    const tabsToRemove: string[] = [];

    this.activeTabs.forEach((lastSeen, tabId) => {
      if (tabId !== this.tabId && now - lastSeen > this.TAB_TIMEOUT_MS) {
        tabsToRemove.push(tabId);
      }
    });

    tabsToRemove.forEach(tabId => this.activeTabs.delete(tabId));
  }

  public requestTokenRefresh(
    requestedByUserId?: string | null,
    trigger: TokenRecoveryTrigger = 'token_demand'
  ): Promise<TokenRefreshAttemptResult> {
    return this.tokenRefresh.requestTokenRefresh(requestedByUserId, trigger);
  }

  public broadcastTokenUpdate(tokenData: TokenRefreshSuccess): void {
    this.tokenRefresh.broadcastTokenUpdate(tokenData);
  }

  public setTokenRefreshCallback(callback: TokenRefreshCallback): void {
    this.tokenRefresh.setTokenRefreshCallback(callback);
  }

  public setTokenUpdateCallback(callback: TokenUpdateCallback): void {
    this.tokenRefresh.setTokenUpdateCallback(callback);
  }

  public setTokenRefreshResultCallback(callback: TokenRefreshResultCallback): void {
    this.tokenRefresh.setTokenRefreshResultCallback(callback);
  }

  public setAuthUserId(userId: string | null): void {
    this.tokenRefresh.setAuthUserId(userId);
  }

  public getAuthUserId(): string | null {
    return this.tokenRefresh.getAuthUserId();
  }

  public setLeadershipChangeCallback(callback: (isLeader: boolean) => void) {
    this.onLeadershipChangeCallback = callback;
  }

  public addLeadershipChangeCallback(callback: LeadershipChangeCallback): () => void {
    this.leadershipChangeCallbacks.add(callback);
    callback(this.isLeadershipActive);

    return () => {
      this.leadershipChangeCallbacks.delete(callback);
    };
  }

  public addMessageListener(type: string, callback: BroadcastMessageHandler): () => void {
    const handlers = this.messageHandlers.get(type) || new Set<BroadcastMessageHandler>();

    handlers.add(callback);
    this.messageHandlers.set(type, handlers);

    return () => {
      handlers.delete(callback);

      if (!handlers.size) {
        this.messageHandlers.delete(type);
      }
    };
  }

  public postMessage(type: string, data: unknown) {
    if (!this.isDestroyed) {
      this.channel.postMessage({ type, data });
    }
  }

  public getIsLeader(): boolean {
    return this.isLeadershipActive;
  }

  public async hasLeader(): Promise<boolean> {
    return this.elector.hasLeader();
  }

  public getTabId(): string {
    return this.tabId;
  }

  public async getTabInfo(): Promise<TabInfo> {
    this.cleanupInactiveTabs();

    return {
      isLeader: this.isLeadershipActive,
      tabCount: this.activeTabs.size,
      leaderId: this.currentLeaderId,
      openedTabsIds: Array.from(this.activeTabs.keys())
    };
  }

  private finishLeadershipWait(hasLeader: boolean) {
    const pendingWait = this.pendingLeadershipWait;
    if (!pendingWait) {
      return;
    }

    clearTimeout(pendingWait.timeoutId);
    this.pendingLeadershipWait = null;
    pendingWait.resolve(hasLeader);
  }

  public ensureLeadership(): Promise<boolean> {
    if (this.isDestroyed || this.isLeadershipActive) {
      return Promise.resolve(!this.isDestroyed);
    }

    if (this.pendingLeadershipWait) {
      return this.pendingLeadershipWait.promise;
    }

    let resolveWait!: (hasLeader: boolean) => void;
    const promise = new Promise<boolean>(resolve => {
      resolveWait = resolve;
    });
    const pendingWait = {
      promise,
      resolve: resolveWait,
      timeoutId: window.setTimeout(() => {
        this.finishLeadershipWait(false);
      }, TOKEN_REFRESH_TIMEOUT_MS)
    };
    this.pendingLeadershipWait = pendingWait;

    // initializeLeadership keeps this tab in the background election. Refresh
    // callers can proceed when ANY tab becomes leader, including another tab.
    this.hasLeader()
      .then(hasLeader => {
        if (this.pendingLeadershipWait !== pendingWait) {
          return;
        }

        if (hasLeader || this.isLeadershipActive) {
          this.finishLeadershipWait(true);
        } else {
          this.requestLeaderInfo();
        }
      })
      .catch(() => {
        if (this.pendingLeadershipWait === pendingWait) {
          console.error('Tab leadership availability check failed');
          this.finishLeadershipWait(false);
        }
      });

    return promise;
  }

  public async destroy() {
    if (this.isDestroyed) {
      return;
    }
    this.isDestroyed = true;
    this.finishLeadershipWait(false);
    this.tokenRefresh.destroy();

    if (this.visibilityTimeout !== null) {
      clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.channel.removeEventListener('message', this.handleChannelMessage);

    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.isLeadershipActive) {
      await this.elector.die();
      this.isLeadershipActive = false;

      this.notifyLeadershipChange(false);
    }

    this.activeTabs.clear();
    this.leadershipChangeCallbacks.clear();
    this.messageHandlers.clear();

    await this.channel.close();
  }
}
